import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import { I18nProvider } from "@/contexts/I18nContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatContextNotesProvider } from "@/contexts/ChatContextNotesContext";
import { AppLayout } from "@/components/AppLayout";
import { captureSource } from "@/lib/attribution";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Pricing from "@/pages/Pricing";
import Jurisprudence from "@/pages/Jurisprudence";
import LegalKnowledge from "@/pages/LegalKnowledge";
import Atti from "@/pages/Atti";
import Marketplace from "@/pages/Marketplace";
import MarketplaceTemplate from "@/pages/MarketplaceTemplate";
import MarketplaceSales from "@/pages/MarketplaceSales";
import MarketplaceLibrary from "@/pages/MarketplaceLibrary";
import MarketplaceSuccess from "@/pages/MarketplaceSuccess";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Dashboard from "@/pages/Dashboard";
import ChatPage from "@/pages/ChatPage";
import ImageStudio from "@/pages/ImageStudio";
import CodeBuilder from "@/pages/CodeBuilder";
import Documents from "@/pages/Documents";
import Referrals from "@/pages/Referrals";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Affiliate from "@/pages/Affiliate";
import Partner from "@/pages/Partner";
import PartnerKit from "@/pages/PartnerKit";
import PartnerLanding from "@/pages/PartnerLanding";
import Activity from "@/pages/Activity";
import Settings from "@/pages/Settings";
import Cookies from "@/pages/Cookies";
import PrivacyCenter from "@/pages/PrivacyCenter";
import Brains from "@/pages/Brains";
import TeamPage from "@/pages/Team";
import TeamAccept from "@/pages/TeamAccept";
import Admin from "@/pages/Admin";
import { CookieConsent } from "@/components/CookieConsent";
import { PreviewOriginGuard } from "@/components/PreviewOriginGuard";
import { InstallPrompt } from "@/components/InstallPrompt";
import { UpdatePrompt } from "@/components/UpdatePrompt";

// Capture acquisition source (?source=qr / utm_source / ref) on initial load
// and persist for 30 days so we can attribute downstream signups/installs.
if (typeof window !== "undefined") {
  captureSource();
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback (session_id in URL hash) synchronously during render.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/legal/jurisprudence" element={<Jurisprudence />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/template/:templateId" element={<MarketplaceTemplate />} />
      <Route path="/marketplace/t/:slug" element={<MarketplaceTemplate />} />
      <Route path="/legal/jurisprudence/:slug" element={<Jurisprudence />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/privacy-center" element={<PrivacyCenter />} />
      <Route path="/brains" element={<Brains />} />
      <Route path="/team/accept" element={<TeamAccept />} />
      <Route path="/affiliate" element={<Affiliate />} />
      <Route path="/p/:code" element={<PartnerLanding />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />

      {/* Protected app */}
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/images" element={<ImageStudio />} />
        <Route path="/code" element={<CodeBuilder />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/legal/knowledge" element={<LegalKnowledge />} />
        <Route path="/legal/atti" element={<Atti />} />
        <Route path="/marketplace/sales" element={<MarketplaceSales />} />
        <Route path="/marketplace/library" element={<MarketplaceLibrary />} />
        <Route path="/marketplace/success" element={<MarketplaceSuccess />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/partner/kit" element={<PartnerKit />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <I18nProvider>
          <BrowserRouter>
            <AuthProvider>
              <ChatContextNotesProvider>
                <AppRouter />
                <CookieConsent />
                <PreviewOriginGuard />
                <InstallPrompt />
                <UpdatePrompt />
                <Toaster position="top-right" theme="dark" />
              </ChatContextNotesProvider>
            </AuthProvider>
          </BrowserRouter>
        </I18nProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
